import React, { useEffect, useRef, useState } from 'react';
import { CreditCard, Loader2 } from 'lucide-react';
import apiService from '../services/api';
import {
  buildLegacyPaiementDisplay,
  buildPrestationPaiementApiPayload,
  buildPrestationPaiementDraft,
  buildMatriceApiPayload,
  isPrestationPaiementDraftDirty,
  CODE_POLITIQUE_PAIEMENT_ACTIVATION_DESACTIVEE,
  formatLegacyActivationLabel,
  getApiErrorCode,
  isPolitiquePaiementParModeActive,
  LEGACY_POLICY_EXPLANATION,
  messageForPolitiquePaiementError,
  resolveMatriceDraft,
  shouldShowPerModeMatrix,
  validateMatriceDraftForModes,
} from '../utils/politiquePaiementParModeUi';

/** Lot A — organisation par mode de commande (icônes = Services). */
const MODE_BLOCS = [
  {
    id: 'livraison',
    title: 'Livraison',
    icon: '🚚',
    rows: [
      { key: 'carteAutoriseeLivraison', label: 'Paiement en ligne' },
      { key: 'surPlaceAutoriseLivraison', label: 'Paiement sur place' },
    ],
  },
  {
    id: 'retrait',
    title: 'Click & Collect',
    icon: '📦',
    rows: [
      { key: 'carteAutoriseeRetrait', label: 'Paiement en ligne' },
      { key: 'surPlaceAutoriseRetrait', label: 'Paiement sur place' },
    ],
  },
];

/** Canal prestations / RDV — flags legacy boutique (paiementEnLigne / paiementSurPlace). */
const PRESTATION_PAIEMENT_ROWS = Object.freeze([
  { key: 'paiementEnLigne', label: 'Paiement en ligne' },
  { key: 'paiementSurPlace', label: 'Paiement sur place' },
]);

/** Switch identique à ServicesConfigurationPanel (peer sr-only + pastille rouge/verte). */
function PaymentSwitch({ checked, disabled, onChange, ariaLabel, testId }) {
  return (
    <label className="relative inline-flex cursor-pointer items-center">
      <input
        type="checkbox"
        checked={checked}
        disabled={disabled}
        onChange={onChange}
        className="peer sr-only"
        aria-label={ariaLabel}
        data-testid={testId}
      />
      <div className="peer h-6 w-11 rounded-full bg-red-500 after:absolute after:left-[2px] after:top-[2px] after:h-5 after:w-5 after:rounded-full after:border after:border-gray-300 after:bg-white after:transition-all peer-checked:bg-green-500 peer-checked:after:translate-x-full peer-checked:after:border-white peer-focus:outline-none peer-focus:ring-4 peer-focus:ring-green-200 peer-disabled:cursor-not-allowed peer-disabled:opacity-50" />
    </label>
  );
}

/**
 * Lot A — Bloc « Moyens de paiement » (UI admin par mode).
 * Matrice via endpoints dédiés — n’appelle pas updateCommercant pour la matrice (champs protégés).
 * Prestations & RDV : updateCommercant sur paiementEnLigne / paiementSurPlace uniquement.
 */
export default function PolitiquePaiementParModePanel({
  commercant,
  onRefresh,
  toast,
}) {
  const active = isPolitiquePaiementParModeActive(commercant);
  const showMatrix = shouldShowPerModeMatrix(commercant);
  const legacyDisplay = buildLegacyPaiementDisplay(commercant);
  const modulePrestationsEnabled = commercant?.modulePrestations === true;
  const [draft, setDraft] = useState(() => resolveMatriceDraft({ commercant }).draft);
  const [prestationDraft, setPrestationDraft] = useState(() =>
    buildPrestationPaiementDraft(commercant),
  );
  const [sessionKeptDraft, setSessionKeptDraft] = useState(null);
  const sessionKeptDraftRef = useRef(null);
  const [busy, setBusy] = useState(false);
  const [activationGateBlocked, setActivationGateBlocked] = useState(false);
  const [eligibilityLoading, setEligibilityLoading] = useState(false);
  const [localError, setLocalError] = useState(null);
  const [savedFlash, setSavedFlash] = useState(false);

  useEffect(() => {
    sessionKeptDraftRef.current = sessionKeptDraft;
  }, [sessionKeptDraft]);

  const commercantId = commercant?.id;
  const serveurCarteLivraison = commercant?.carteAutoriseeLivraison === true;
  const serveurSurPlaceLivraison = commercant?.surPlaceAutoriseLivraison === true;
  const serveurCarteRetrait = commercant?.carteAutoriseeRetrait === true;
  const serveurSurPlaceRetrait = commercant?.surPlaceAutoriseRetrait === true;
  const serveurPaiementEnLigne = commercant?.paiementEnLigne === true;
  const serveurPaiementSurPlace = commercant?.paiementSurPlace === true;

  useEffect(() => {
    const resolved = resolveMatriceDraft({
      commercant,
      sessionKeptDraft: sessionKeptDraftRef.current,
    });
    setDraft(resolved.draft);
    if (resolved.clearSessionKept) {
      setSessionKeptDraft(null);
      sessionKeptDraftRef.current = null;
    }
    setSavedFlash(false);
    setLocalError(null);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [
    commercantId,
    active,
    serveurCarteLivraison,
    serveurSurPlaceLivraison,
    serveurCarteRetrait,
    serveurSurPlaceRetrait,
  ]);

  useEffect(() => {
    setPrestationDraft(buildPrestationPaiementDraft(commercant));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [commercantId, serveurPaiementEnLigne, serveurPaiementSurPlace]);

  const validation = validateMatriceDraftForModes({
    livraison: commercant?.livraison === true,
    clickAndCollect: commercant?.clickAndCollect === true,
    matrice: draft,
  });
  const matriceValide = validation.ok === true;
  // Persistance possible si déjà active (update matrice) OU gate d’activation ouvert.
  const canPersist = active || !activationGateBlocked;
  const prestationDirty = modulePrestationsEnabled
    && isPrestationPaiementDraftDirty(prestationDraft, commercant);
  const matrixSaveReady =
    showMatrix && matriceValide && canPersist && !eligibilityLoading;
  const prestationSaveReady = modulePrestationsEnabled && prestationDirty;
  const canSave =
    !busy &&
    !eligibilityLoading &&
    (matrixSaveReady || prestationSaveReady);

  const toggleField = (key) => {
    if (busy || (!active && activationGateBlocked)) return;
    setSavedFlash(false);
    setLocalError(null);
    setDraft((prev) => ({ ...prev, [key]: !prev[key] }));
  };

  const togglePrestationField = (key) => {
    if (busy) return;
    setSavedFlash(false);
    setLocalError(null);
    setPrestationDraft((prev) => ({ ...prev, [key]: !prev[key] }));
  };

  const persistPrestationPaiementIfNeeded = async () => {
    if (!modulePrestationsEnabled || !prestationDirty) return;
    await apiService.updateCommercant(
      commercant.id,
      buildPrestationPaiementApiPayload(prestationDraft),
    );
  };

  const renderPrestationPaiementBloc = () => {
    if (!modulePrestationsEnabled) return null;
    return (
      <div
        className="rounded-lg border-2 border-gray-200 bg-gray-50/50 p-4"
        data-testid="politique-paiement-bloc-prestation"
      >
        <div className="mb-3 flex items-center gap-2">
          <span className="text-2xl" aria-hidden>
            📅
          </span>
          <h5 className="text-sm font-semibold text-gray-900">Prestations &amp; RDV</h5>
        </div>
        <div className="space-y-2">
          {PRESTATION_PAIEMENT_ROWS.map((row) => {
            const on = prestationDraft[row.key] === true;
            return (
              <div
                key={row.key}
                className={`rounded-lg border p-3 transition-colors ${
                  on ? 'border-green-200 bg-green-50' : 'border-red-100 bg-white'
                }`}
              >
                <div className="flex items-center justify-between gap-3">
                  <span className="text-sm font-medium text-gray-900">{row.label}</span>
                  <PaymentSwitch
                    checked={on}
                    disabled={busy}
                    onChange={() => togglePrestationField(row.key)}
                    ariaLabel={`Prestations & RDV — ${row.label}`}
                    testId={`prestation-${row.key}`}
                  />
                </div>
              </div>
            );
          })}
        </div>
      </div>
    );
  };

  useEffect(() => {
    let cancelled = false;
    async function loadEligibility() {
      if (!commercantId) return;
      // Déjà active : update matrice autorisé (pas de gate d’activation).
      if (active) {
        setActivationGateBlocked(false);
        setEligibilityLoading(false);
        return;
      }
      setEligibilityLoading(true);
      try {
        const eligibility =
          await apiService.getPolitiquePaiementActivationEligibility(commercantId);
        if (cancelled) return;
        if (eligibility?.allowed === true) {
          setActivationGateBlocked(false);
          setLocalError(null);
        } else {
          setActivationGateBlocked(true);
          setLocalError(
            eligibility?.message ||
              'L’enregistrement n’est pas disponible pour ce commerce (activation non autorisée).',
          );
        }
      } catch (err) {
        if (cancelled) return;
        // Fail-closed UI : pas de CTA trompeur si l’éligibilité est inconnue.
        setActivationGateBlocked(true);
        setLocalError(
          messageForPolitiquePaiementError(err) ||
            'Impossible de vérifier l’autorisation d’activation.',
        );
      } finally {
        if (!cancelled) setEligibilityLoading(false);
      }
    }
    loadEligibility();
    return () => {
      cancelled = true;
    };
  }, [commercantId, active]);

  const refreshFromServer = async () => {
    if (typeof onRefresh === 'function') {
      await onRefresh();
    }
  };

  const runAction = async (action, successMessage) => {
    if (busy) return;
    setBusy(true);
    setLocalError(null);
    setSavedFlash(false);
    let mutationSucceeded = false;
    try {
      await action();
      mutationSucceeded = true;
      await refreshFromServer();
      setSavedFlash(true);
      if (successMessage) {
        toast?.success?.(successMessage);
      }
    } catch (err) {
      setSavedFlash(false);
      if (mutationSucceeded) {
        const msg =
          'L’enregistrement a réussi, mais le rechargement des données a échoué. Actualisez la page.';
        setLocalError(msg);
        toast?.error?.(msg);
      } else {
        const code = getApiErrorCode(err);
        if (code === CODE_POLITIQUE_PAIEMENT_ACTIVATION_DESACTIVEE) {
          setActivationGateBlocked(true);
        }
        const msg = messageForPolitiquePaiementError(err);
        setLocalError(msg);
        toast?.error?.(msg);
      }
    } finally {
      setBusy(false);
    }
  };

  const handleEnregistrer = async () => {
    const shouldSaveMatrix = showMatrix && matrixSaveReady;
    if (!shouldSaveMatrix && !prestationSaveReady) {
      if (showMatrix && !matriceValide) {
        setLocalError(
          'Pour chaque mode de commande actif, choisissez au moins un moyen de paiement.'
        );
      }
      return;
    }
    if (!shouldSaveMatrix && prestationSaveReady) {
      await runAction(async () => {
        await persistPrestationPaiementIfNeeded();
      }, 'Moyens de paiement enregistrés');
      return;
    }

    const payload = buildMatriceApiPayload(draft);
    if (!active) {
      await runAction(async () => {
        await apiService.activerPolitiquePaiementParMode(commercant.id, payload);
        await persistPrestationPaiementIfNeeded();
      }, 'Moyens de paiement enregistrés');
      return;
    }
    await runAction(async () => {
      await apiService.updateMatricePolitiquePaiementParMode(
        commercant.id,
        payload
      );
      await persistPrestationPaiementIfNeeded();
    }, 'Moyens de paiement enregistrés');
  };

  if (!commercant?.id) return null;

  return (
    <div
      className="mt-6 rounded-lg border border-gray-200 bg-white p-6 shadow-sm"
      data-testid="politique-paiement-par-mode-panel"
    >
      <div className="mb-6 flex items-start gap-3">
        <div className="rounded-lg bg-blue-100 p-2 text-blue-700">
          <CreditCard className="h-5 w-5" aria-hidden />
        </div>
        <div className="min-w-0 flex-1">
          <h3 className="text-lg font-semibold text-gray-900">
            Moyens de paiement
          </h3>
          <p className="mt-1 text-sm text-gray-500">
            {showMatrix
              ? 'Choisissez les moyens de paiement disponibles pour chaque mode de commande.'
              : 'Configuration générale actuellement utilisée par cette boutique.'}
          </p>
        </div>
      </div>

      {showMatrix ? (
        <>
      <div className="space-y-4" data-testid="politique-paiement-matrice-blocs">
        {MODE_BLOCS.map((bloc) => (
          <div
            key={bloc.id}
            className="rounded-lg border-2 border-gray-200 bg-gray-50/50 p-4"
            data-testid={`politique-paiement-bloc-${bloc.id}`}
          >
            <div className="mb-3 flex items-center gap-2">
              <span className="text-2xl" aria-hidden>
                {bloc.icon}
              </span>
              <h5 className="text-sm font-semibold text-gray-900">{bloc.title}</h5>
            </div>
            <div className="space-y-2">
              {bloc.rows.map((row) => {
                const on = draft[row.key] === true;
                return (
                  <div
                    key={row.key}
                    className={`rounded-lg border p-3 transition-colors ${
                      on
                        ? 'border-green-200 bg-green-50'
                        : 'border-red-100 bg-white'
                    }`}
                  >
                    <div className="flex items-center justify-between gap-3">
                      <span className="text-sm font-medium text-gray-900">
                        {row.label}
                      </span>
                      <PaymentSwitch
                        checked={on}
                        disabled={busy || eligibilityLoading || (!active && activationGateBlocked)}
                        onChange={() => toggleField(row.key)}
                        ariaLabel={`${bloc.title} — ${row.label}`}
                        testId={`matrice-${row.key}`}
                      />
                    </div>
                  </div>
                );
              })}
            </div>
          </div>
        ))}
        {renderPrestationPaiementBloc()}
      </div>

      {!matriceValide && (
        <p className="mt-3 text-sm text-amber-700" role="status">
          {validation.modesActifsSansMoyen.includes('livraison') &&
            'Livraison : choisissez au moins un moyen de paiement. '}
          {validation.modesActifsSansMoyen.includes('clickandcollect') &&
            'Click & Collect : choisissez au moins un moyen de paiement.'}
        </p>
      )}

      {localError && (
        <p
          className="mt-3 text-sm text-red-700"
          role="alert"
          data-testid="politique-paiement-error"
        >
          {localError}
        </p>
      )}

      {savedFlash && (
        <p
          className="mt-3 text-sm text-green-700"
          role="status"
          data-testid="politique-paiement-saved"
        >
          Modifications enregistrées.
        </p>
      )}

      <div className="mt-4 flex flex-wrap gap-2">
        <button
          type="button"
          onClick={handleEnregistrer}
          disabled={!canSave}
          className="inline-flex items-center gap-2 rounded-md bg-blue-600 px-3 py-2 text-sm font-medium text-white hover:bg-blue-700 disabled:opacity-50"
          data-testid="politique-paiement-enregistrer"
        >
          {busy && <Loader2 className="h-4 w-4 animate-spin" aria-hidden />}
          Enregistrer les modifications
        </button>
      </div>
        </>
      ) : modulePrestationsEnabled ? (
        <>
          <p className="text-sm text-gray-600">{LEGACY_POLICY_EXPLANATION}</p>
          <div className="mt-4 space-y-4">{renderPrestationPaiementBloc()}</div>
          {localError && (
            <p
              className="mt-3 text-sm text-red-700"
              role="alert"
              data-testid="politique-paiement-error"
            >
              {localError}
            </p>
          )}
          {savedFlash && (
            <p
              className="mt-3 text-sm text-green-700"
              role="status"
              data-testid="politique-paiement-saved"
            >
              Modifications enregistrées.
            </p>
          )}
          <div className="mt-4 flex flex-wrap gap-2">
            <button
              type="button"
              onClick={handleEnregistrer}
              disabled={!canSave}
              className="inline-flex items-center gap-2 rounded-md bg-blue-600 px-3 py-2 text-sm font-medium text-white hover:bg-blue-700 disabled:opacity-50"
              data-testid="politique-paiement-enregistrer"
            >
              {busy && <Loader2 className="h-4 w-4 animate-spin" aria-hidden />}
              Enregistrer les modifications
            </button>
          </div>
        </>
      ) : (
        <div data-testid="politique-paiement-legacy-readonly">
          <h4 className="text-sm font-semibold text-gray-900">Configuration actuelle</h4>
          <p className="mt-2 text-sm text-gray-600">{LEGACY_POLICY_EXPLANATION}</p>
          <div className="mt-4 space-y-2">
            <div
              className="flex items-center justify-between rounded-lg border border-gray-200 bg-gray-50 p-3"
              data-testid="politique-paiement-legacy-enligne"
            >
              <span className="text-sm font-medium text-gray-900">Paiement en ligne</span>
              <span
                className={`text-sm font-semibold ${
                  legacyDisplay.paiementEnLigne ? 'text-green-700' : 'text-gray-600'
                }`}
              >
                {formatLegacyActivationLabel(legacyDisplay.paiementEnLigne)}
              </span>
            </div>
            <div
              className="flex items-center justify-between rounded-lg border border-gray-200 bg-gray-50 p-3"
              data-testid="politique-paiement-legacy-surplace"
            >
              <span className="text-sm font-medium text-gray-900">Paiement sur place</span>
              <span
                className={`text-sm font-semibold ${
                  legacyDisplay.paiementSurPlace ? 'text-green-700' : 'text-gray-600'
                }`}
              >
                {formatLegacyActivationLabel(legacyDisplay.paiementSurPlace)}
              </span>
            </div>
          </div>
          {activationGateBlocked && (
            <p
              className="mt-3 text-sm text-amber-800"
              data-testid="politique-paiement-gate-blocked"
              role="status"
            >
              {localError ||
                'L’enregistrement n’est pas disponible pour ce commerce (activation non autorisée). Les interrupteurs ne peuvent pas être persistés.'}
            </p>
          )}
        </div>
      )}
    </div>
  );
}
